import React, {useEffect, useState} from 'react';
import {CircularProgress, Paper} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {useTranslation} from 'react-i18next';
import {useDebounce} from 'use-debounce';
import CoreData from '../../app/coreDataUtils';
import EditorMarkdownComponent from '../editor/editorMarkdown';
import config from '../../app/config';
import moment from 'moment';
import clsx from 'clsx';
import MessageBox from '../../util/messageBox';
import IconButton from '@mui/material/IconButton';
import {useDispatch, useSelector} from 'react-redux';
import {hasPermission, Permissions, user} from '../../store/reducers/user';
import InputAdornment from '@mui/material/InputAdornment';
import AgyleTextField from '../ui/fields/agyleTextField';
import AgyleButton from '../ui/buttons/agyleButton';
import AgyleDialog from '../modal/agyleDialog';
import InformationBox from '../other/informationBox';
import {isEmpty} from 'lodash';
import useOpenState from '../../hooks/useOpenState';
import HistoryIcon from '@mui/icons-material/History';
import {AgyleBinIcon} from '../icons/agyle';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
    },
    row: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    textField: {
        width: '100%',
    },
    options: {
        marginBottom: 12,
        '& .option': {
            fontSize: 'small',
            display: 'inline',
            marginRight: 10,
            fontWeight: 'bold',
            // color: theme.palette.primary.main,
            color: theme.palette.secondary.main,
            cursor: 'pointer',
            textTransform: 'uppercase',
            '&.color-same': {
                color: theme.palette.primary.main,
            },
        },
    },
    coreText: {
        opacity: .5,
        fontSize: '90%',
    },
    modalBody: {
        width: '60%',
        left: '20%',
        position: 'absolute',
        top: 'calc(50vh - 90px)',
    },
    historyLabel: {
        '& .label-part1': {
            fontWeight: 'bold',
        },
        '& .label-part2': {
            fontWeight: 'normal',
            marginRight: 10,
            wordBreak: 'break-word',
        },
        '& .label-part3': {
            fontSize: '90%',
            opacity: .7,
            whiteSpace: 'break-spaces',
        },
    },
    disabled: {
        pointerEvents: 'none',
        opacity: '0.5',
    },
    box: {
        width: '100%',
        borderRadius: 10,
        overflow: 'hidden',
        padding: 10,
        paddingLeft: 15,
        backgroundColor: theme.palette.background.paperDarker,
        marginBottom: 10,
    },
    historyButtonsSection: {
        marginLeft: 'auto',
        display: 'flex',
        paddingBottom: 8,
    },
    historyButton: {
        margin: '2px 5px',
        padding: '4px 8px 0px 8px',
    },
    closeButton: {
        margin: '2px 5px',
        padding: '4px 8px 0px 8px',
        float: 'left',
        color: 'white',
    },
    modalContent: {
        margin: '2px 5px',
        padding: '14px 22px 10px 22px',
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    bin: {
        border: '1px solid #E25B61',
        borderRadius: 8,
        color: '#E25B61',
    },
    dialogBackgroundColor: {
        backgroundColor: `${theme.palette.mode === 'light' ? '#ffffff' : '#313233'} !important`,
    },
    historyEntry: {
        backgroundColor: theme.palette.mode === 'light' ? '#D2D5D9' : '#3D3E40',
        padding: 10,
        marginBottom: 10,
        borderRadius: 6,
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.mode === 'light' ? '#dee1e5' : '#494b4c',
        },
    },
    title: {
        color: '#acafb3',
        fontWeight: 'bold',
    },
    prevEntry: {
        color: theme.palette.mode === 'dark' ? '#ACAFB3' : '#222425',
    },
    addButton: {
        color: theme.palette.primary.main,
        cursor: 'pointer',
        fontWeight: 'bold',
        '&.disabled': {
            color: theme.palette.secondary.main,
            cursor: 'auto',
        },
    },
    shortText: {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
    }
}));

function CoreDataTextField(props) {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const userObject = useSelector(user);


    const {
        doNotUpdate,
        showCoreValue,
        autoFillEmpty,
        hidePreview,
        instanceId,
        showHistoryAsDialog,
        patientTriageHistory,
        hideHistory,
        guideAction,
        ...tbProps
    } = props;

    // Core DS states
    const [coreData, setCoreData] = React.useState(null);
    const [history, setHistory] = React.useState(null);
    const [inputValue, setInputValue] = React.useState(props.value ? props.value : '');

    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [init, setInit] = React.useState(true);

    const markdownRef = React.useRef(null);
    const isMounted = React.useRef(true);
    const controller = new AbortController();

    const [inputValueDebounced] = useDebounce(inputValue, 2000);
    const [hisValToAdd, setHisValToAdd] = React.useState(null);
    const [deleteHist, setDeleteHist] = React.useState(null);

    // Open states
    const [isHistoryOpen, openHistory, closeHistory] = useOpenState(false, true);
    const [isConfirmDeletedOpen, openConfirmDeleted, closeConfirmDeleted] = useOpenState(false, true);
    const [isTriageHistoryOpen, openTriageHistory, closeTriageHistory] = useOpenState(false, true);
    const [isHistoryValPromptOpen, openHistoryValPrompt, closeHistoryValPrompt] = useOpenState(false, true);

    // Show large text
    const [toggleText, setToggleText] = useState(false);

    useEffect(() => {
        return () => {
            isMounted.current = false;
            controller.abort();
        };
    }, []);

    useEffect(() => {
        if (props.value) {
            setInputValue(props.value);
        }
    }, [props.value]);

    useEffect(() => {
        const go = async () => {
            if (isMounted.current && !doNotUpdate && inputValueDebounced && inputValueDebounced.length > 0) {
                setSaving(true);
                // this is not important even if that will not be save for some reason ...
                let data = await CoreData.handleDataChange(props.patient, props.path, {
                        data: {
                            value: inputValueDebounced,
                        },
                    }, instanceId, controller.signal)
                    .finally(() => setSaving(false));
                setCoreData(data?.data || null);
            }
        };
        if (!init) {
            go();
        } else {
            setInit(false);
        }
    }, [inputValueDebounced]);

    // load data on init ()
    useEffect(() => {
        go();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.patient, props.path]);

    useEffect(() => {
        if (props.autoFillEmpty && coreData) {
            if (!inputValue || inputValue === '') {
                setInputValue(coreData.value);
            }
        }
    }, [coreData, props.autoFillEmpty]);


    const inputChanged = event => {
        setInputValue(event.target.value);
    };

    const dataChanged = event => {
        if (props.onChange)
            props.onChange(event);

        setInputValue(event.target.value);
    };

    const go = async () => {
        setLoading(true);
        if (isMounted.current && props?.patient && props?.path) {
            // CoreData
            let data = await CoreData.getData(props.patient, props.path, controller.signal);
            setCoreData(data?.data || null);

            await CoreData.getHist(props.patient, props.path, controller.signal).then(data => {
                setHistory(data);
            });

            setLoading(false);
        } else {
            setCoreData(null);
            setHistory(null);
            setLoading(false);
        }
    };

    const resetToCoreValue = () => {
        closeHistoryValPrompt();
        setInputValue(hisValToAdd || coreData?.value);
        resetToHistValue(hisValToAdd || coreData?.value);
    };

    const resetToHistValue = (val) => {
        setInputValue(val);
        if (props.useMarkdown) {
            if (markdownRef?.current) {
                markdownRef.current.updateValue(val);
            }
        }

        if (props.onChange) {
            props.onChange({
                target: {
                    value: val,
                },
            });
        }
        setHisValToAdd(null);
    };

    const copyFromTriageHistory = () => {
        setInputValue(props.patientTriageHistory);
        closeTriageHistory();
    };

    const asqHistValueClick = (val) => {
        closeHistory();
        openHistoryValPrompt();
        setHisValToAdd(val);
    };

    const deleteHistory = () => {
        closeConfirmDeleted();
        if (deleteHist) {
            CoreData.deleteHist(props.patient, deleteHist?.id, controller.signal).then(response => {
                if (response) {
                    setDeleteHist(null);
                    closeHistory();
                    go();
                }
            });
        }
    };

    const renderEndAdornment = () => {
        return (
            <>
                {loading &&
                    <CircularProgress style={{color: '#fff'}} size={20}/>
                }

                <InputAdornment position="end">{t(saving ? 'Saving...' : '')}</InputAdornment>

                {(!loading && coreData && !hideHistory) &&
                    <InputAdornment position="end">
                        <IconButton
                            onClick={openHistory}
                            disabled={props.disabled}
                            color="primary"
                        >
                            <HistoryIcon/>
                        </IconButton>
                    </InputAdornment>
                }
            </>
        );
    };

    const handleChooseHistoryVal = (e, val) => {
        const disableSelect = e.target.closest('.disabled-options') !== null;
        if (!disableSelect) {
            asqHistValueClick(val);
        }
    };

    const historyModal = () => {
        return (
            <div className={'scroll-normal-white'}>
                {isEmpty(history) &&
                    <InformationBox type="info">
                        {t('There are no historical entry.')}
                    </InformationBox>
                }

                {!isEmpty(history) && history?.map(item => (
                        <div
                            key={item.id}
                            className={classes.historyEntry}
                            onClick={(e) => handleChooseHistoryVal(e, item.val)}
                        >
                            <div className={classes.divRow} style={{justifyContent: 'space-between'}}>
                                <div style={{fontWeight: 'bold'}}>
                                    {item.val}
                                </div>

                                <div className={clsx('disabled-options', classes.bin)}>
                                    <IconButton
                                        disabled={!(item?.createdByLogin === userObject?.userName || dispatch(hasPermission(Permissions.AGYLE_CAN_MENAGE_PREVIOUS_VALUES)))}
                                        onClick={() => {
                                            setDeleteHist(item);
                                            openConfirmDeleted();
                                        }}
                                    >
                                        <AgyleBinIcon style={{color: '#E25B61', fontSize: 'small'}}/>
                                    </IconButton>
                                </div>
                            </div>

                            <div className={classes.divRow} style={{gap: 10}}>
                                <div className={classes.title}>
                                    {t('When')}
                                </div>

                                <div>
                                    {moment(item.createdDate).format(config.defaultDateTimeFormat)}
                                </div>

                                <div className={classes.title}>
                                    {t('Who')}
                                </div>

                                <div>
                                    {item.createdBy}
                                </div>
                            </div>
                        </div>
                    ),
                )}
            </div>
        );
    };

    const renderPreviousEntry = () => {
        const handleToggle = () => {
            setToggleText(!toggleText);
        };

        return (
            <div className={classes.divRow} style={{fontSize: 'small', gap: 5, marginBottom: 5, alignItems: 'flex-end'}}>
                <span className={classes.prevEntry}>
                    {t('previous entry')}{' - '}
                </span>

                <span
                    className={clsx((coreData?.value?.length > 50 && !toggleText) && classes.shortText)}
                    style={{fontWeight: 'bold', maxWidth: 600, cursor: 'pointer'}}
                    onClick={handleToggle}
                >
                    {coreData.value}
                </span>

                <span>
                    {`(${moment(coreData?.when).format(config.defaultDateTimeFormat)}) - ${coreData?.by || ''}`}
                </span>

                <div
                    onClick={() => {
                        if (!props.disabled) openHistoryValPrompt();
                    }}
                    className={clsx(classes.addButton, props.disabled && 'disabled')}
                >
                    {t('Add')}
                </div>
            </div>
        );
    };

    return (
        <div className={classes.root}>
            <div className={classes.row}>
                {(!loading && coreData && !hideHistory && !props.showHistoryAsDialog) && renderPreviousEntry()}

                {(!loading && coreData && props.showHistoryAsDialog) &&
                    <div className={classes.historyButtonsSection}>
                        <AgyleButton
                            variant="text"
                            colour="primary"
                            onClick={openTriageHistory}
                            disabled={props.disabled || (props.patientTriageHistory == null) || (props.patientTriageHistory.length === 0)}
                            size="small"
                        >
                            {t('TRIAGE HoPC')}
                        </AgyleButton>
                    </div>
                }

                {(!loading && !!guideAction && config.clerkingExaminationGuide) &&
                    <div className={classes.historyButtonsSection}>
                        <AgyleButton
                            variant="text"
                            colour="primary"
                            onClick={guideAction}
                            disabled={props.disabled}
                            size="small"
                        >
                            {t('Guide')}
                        </AgyleButton>
                    </div>
                }
            </div>

            {!props.useMarkdown &&
                <AgyleTextField
                    {...tbProps}
                    className={classes.textField}
                    onChange={inputChanged}
                    fullWidth
                    onBlur={dataChanged}
                    value={inputValue}
                    endAdornment={renderEndAdornment()}
                />
            }

            {props.useMarkdown &&
                <EditorMarkdownComponent
                    {...tbProps}
                    ref={markdownRef}
                    onChange={dataChanged}
                    code={inputValue}/>
            }

            <MessageBox
                title={t('Confirmation')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={resetToCoreValue}
                onClose={closeHistoryValPrompt}
                message={t('Please ensure this was correct up to date information.')}
                open={isHistoryValPromptOpen}
                disableBackgroundClick
            />

            <MessageBox
                title={t('Confirmation')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={deleteHistory}
                onClose={closeConfirmDeleted}
                message={t('Are you sure you want to delete this entry')}
                open={isConfirmDeletedOpen}
            />

            <AgyleDialog
                title={t('Previous entries')}
                closeButtonOnHeader
                disableSave
                open={isHistoryOpen}
                onClose={closeHistory}
                width="md"
                fullWidth
                disableActions
                contentStyle={classes.dialogBackgroundColor}
                titleStyle={classes.dialogBackgroundColor}
            >
                {historyModal()}
            </AgyleDialog>

            <AgyleDialog
                disableTitle
                open={isTriageHistoryOpen}
                onSave={copyFromTriageHistory}
                onClose={closeTriageHistory}
                width="sm"
            >
                <Paper className={clsx(classes.box, classes.historyLabel, 'label-part1')}>
                    {patientTriageHistory}
                </Paper>
            </AgyleDialog>
        </div>
    );
}

export {CoreDataTextField};
